import { TOAST_MENU_SECTIONS, type ToastMenuItem } from "@/lib/data/toast-menu"

/**
 * Search landing pages (/salt-bread, /brunch, /matcha): one page per thing
 * people already search for near +Bake. Each is plain data rendered by
 * components/landing-page.tsx, and its route builds title, meta and JSON-LD
 * from the same object, so the visible FAQ and the FAQPage schema can't drift.
 */

/** Internal destinations a landing page may link to. */
export type LandingLinkTarget =
  | "about"
  | "brunch"
  | "coffee"
  | "salt-bread"
  | "matcha"
  | "menu"
  | "visit"

export type LandingLink = {
  label: string
  to: LandingLinkTarget
}

/**
 * A menu item shown on a landing page. `name` must match a Toast item in
 * toast-menu.ts exactly: price and description are read from there, so a menu
 * sync updates these pages too. `note` replaces the Toast description when the
 * page needs its own line.
 */
export type LandingItem = {
  name: string
  label?: string
  note?: string
  image?: string
  imageAlt?: string
}

export type LandingSection = {
  id: string
  heading: string
  paragraphs: string[]
  items?: LandingItem[]
  links?: LandingLink[]
  /**
   * Link to /catering, rendered only while the catering page is live (it is a
   * runtime toggle and 307s to /contact while off). While hidden, the page
   * points at the contact form instead.
   */
  cateringLink?: string
}

export type LandingFaq = { q: string; a: string }

export type LandingPage = {
  path: "/salt-bread" | "/brunch" | "/matcha"
  breadcrumb: string
  title: string
  description: string
  eyebrow: string
  h1: string
  intro: string
  /** 4:5 WebP set in public/images/plusbake/landing/, see heroImage() */
  hero: { src: string; srcSet: string; alt: string; width: number; height: number }
  sections: LandingSection[]
  faq: LandingFaq[]
  /** analytics `location` for the Toast CTA */
  trackingLocation: string
}

/** Hero fields for a `<name>-{480,720,960}.webp` set in images/plusbake/landing. */
export function heroImage(name: string, alt: string): LandingPage["hero"] {
  const base = `/images/plusbake/landing/${name}`
  return {
    src: `${base}-960.webp`,
    srcSet: [480, 720, 960].map((w) => `${base}-${w}.webp ${w}w`).join(", "),
    alt,
    width: 960,
    height: 1200,
  }
}

const TOAST_ITEMS_BY_NAME = new Map<string, ToastMenuItem>()
for (const section of TOAST_MENU_SECTIONS) {
  for (const item of section.items) {
    if (!TOAST_ITEMS_BY_NAME.has(item.name)) {
      TOAST_ITEMS_BY_NAME.set(item.name, item)
    }
  }
}

export function findToastItem(name: string): ToastMenuItem | undefined {
  return TOAST_ITEMS_BY_NAME.get(name)
}
